/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */
// หน้า "ดาวน์โหลดรูป" (25 ต.ค. 2569 · ลูกค้าขอโหลดรูปแยกตามสินค้า/แคมเปญเป็นชุด)
//   ออกแบบใหม่ 1 พ.ย. 2569 (เจ้าของงาน: "ข้อมูลสินค้าเยอะ ต้องมีฟิลเตอร์ หมวด/สาขา/รูป AI" · แบบใน Claude Design):
//   ซ้าย = แท็บ สินค้า/แคมเปญ → ชุดกรองด่วน + ตัวกรอง (ExportFilters.jsx) → จำนวนที่พบ + "เลือกทั้งหน้า" / "เลือกทั้งหมดที่กรอง"
//          → ผลเป็นกริดรูปย่อ (ค่าตั้งต้น) หรือตาราง · เรียงได้ · ติ๊กข้ามหน้าได้
//   ขวา  = ที่เลือก + ตัวเลือกไฟล์ (รูปหลักอย่างเดียว · ที่มาของรูป ทั้งหมด/AI/ไม่ใช่ AI) → สรุปไฟล์คำนวณให้เองเมื่อที่เลือกเปลี่ยน
//          → "ดูรายการไฟล์ก่อนโหลด" (ExportFileReview.jsx · ติ๊กออกทีละไฟล์) → "ดาวน์โหลด zip"
//   zip รวมในเบราว์เซอร์ด้วย _entrio/imageDownload.js แยกโฟลเดอร์ <รหัสสินค้า>/ หรือ <รหัสแคมเปญ>/<รหัสสินค้า>/
//   backend: controllers/image_export.con.php (อ่านอย่างเดียว) · เมนู migration 0032
import React, { useEffect, useRef, useState } from 'react';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Pagination from '@mui/material/Pagination';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDProgress from 'components/MDProgress';
import MDSnackbar from 'components/MDSnackbar';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import { downloadZip, zipDoneText } from 'layouts/_entrio/imageDownload';
import ExportFilters, {
  FilterChips,
  PRODUCT_DEFAULT,
  CAMPAIGN_DEFAULT,
  PRODUCT_PRESETS,
  CAMPAIGN_PRESETS,
  myBranchPreset,
  productChips,
  campaignChips,
  productBody,
  campaignBody,
} from './ExportFilters';
import ExportFileReview, { mb } from './ExportFileReview';

const NET_ERROR = 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ ลองใหม่อีกครั้ง';
const PER_PAGE_OPTIONS = [24, 48, 96];
const SORTS = [
  [
    ['sku', 'รหัสสินค้า'],
    ['name', 'ชื่อสินค้า'],
    ['updated', 'รูปอัปเดตล่าสุด'],
    ['images', 'รูปมากสุด'],
    ['new', 'สินค้าใหม่ล่าสุด'],
  ],
  [
    ['new', 'แคมเปญล่าสุด'],
    ['stop', 'วันหมดอายุใกล้สุด'],
    ['code', 'รหัสแคมเปญ'],
  ],
];
const STATUS_CHIP = {
  active: ['ใช้อยู่', 'success'],
  soon: ['ใกล้หมดอายุ', 'warning'],
  upcoming: ['ยังไม่เริ่ม', 'info'],
  expired: ['หมดอายุแล้ว', 'default'],
};
const EMPTY_META = {
  departments: [],
  categories: [],
  groups: [],
  subgroups: [],
  brands: [],
  branches: [],
  my_branch_id: 0,
};
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(
    d.getDate()
  ).padStart(2, '0')}`;
};
const readView = () => {
  try {
    return localStorage.getItem('image_export.view') === 'table' ? 'table' : 'grid';
  } catch (e) {
    return 'grid';
  }
};
const dateOf = (v) => (v ? Func.dateDisplay(v) : '');

function ImageExport() {
  const mod = 'image_export';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const alive = useRef(true);
  const reqId = useRef(0);
  const timer = useRef(null); // หน่วงช่องพิมพ์ 400ms
  const pending = useRef(null); // ตัวกรองที่รอยิงจากช่องพิมพ์ — กดเปลี่ยนหน้าตอนนี้ต้องเริ่มหน้า 1 ของคำค้นใหม่ (รีเช็ค 27 ต.ค. 2569)
  const toastTimer = useRef(null);
  const checkSeq = useRef(0);

  const [limits, setLimits] = useState({ max_select: 100, max_files: 400 });
  const [meta, setMeta] = useState(EMPTY_META);
  const [tab, setTab] = useState(0); // 0 = สินค้า · 1 = แคมเปญ
  const [view, setView] = useState(readView);
  const [perPage, setPerPage] = useState(24);
  const [sort, setSort] = useState(['sku', 'new']);
  const [pf, setPf] = useState(PRODUCT_DEFAULT);
  const [cf, setCf] = useState(CAMPAIGN_DEFAULT);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState({}); // { id: row }
  const [campaigns, setCampaigns] = useState({});
  const [opt, setOpt] = useState({
    productImages: true,
    campaignImages: true,
    mainOnly: false,
    source: '',
  });
  const [list, setList] = useState(null); // ผลตรวจรายการไฟล์ { files, total, bytes, skipped }
  const [checking, setChecking] = useState(false);
  const [excluded, setExcluded] = useState({}); // { fileId: true } ไฟล์ที่ติ๊กออกในหน้าต่างรายการไฟล์
  const [review, setReview] = useState(false);
  const [picking, setPicking] = useState(false); // กำลังดึง "เลือกทั้งหมดที่กรอง"
  const [progress, setProgress] = useState(null); // [done, total]
  const [notify, setNotify] = useState(null);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        open
        onClose={() => setNotify(null)}
        close={() => setNotify(null)}
        bgWhite
      />
    );
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(
      () => {
        if (alive.current) setNotify(null);
      },
      color === 'error' ? 8000 : 3000
    );
  };

  const filterOf = (which) => (which === 0 ? pf : cf);
  const bodyOf = (which, f) => (which === 0 ? productBody(f) : campaignBody(f));

  const load = (which, f, p, s, per) => {
    reqId.current += 1;
    const id = reqId.current;
    setLoading(true);
    FetchData('OPTIONS', `/${mod}/${which === 0 ? 'products' : 'campaigns'}`, app.token, {
      ...bodyOf(which, f),
      page: p,
      limit: per,
      sort: s,
      _t: Date.now(),
    })
      .then((r) => {
        if (!alive.current || id !== reqId.current) return;
        setLoading(false);
        if (r.status === 'ok') {
          setRows(r.data || []);
          setTotal(Number(r.total || 0));
          setSummary(r.summary || null);
        } else {
          setRows([]);
          setTotal(0);
          setSummary(null);
          showNotify(t(`alert.loaderror`), 'error');
        }
      })
      .catch(() => {
        if (alive.current && id === reqId.current) {
          setLoading(false);
          showNotify(NET_ERROR, 'error');
        }
      });
  };

  useEffect(() => {
    alive.current = true;
    i18n.changeLanguage(app.lang);
    FetchData('GET', `/${mod}`, app.token, {})
      .then((r) => {
        if (alive.current && r.status === 'ok')
          setLimits({
            max_select: Number(r.max_select) || 100,
            max_files: Number(r.max_files) || 400,
          });
      })
      .catch(() => {});
    FetchData('OPTIONS', `/${mod}/filters`, app.token, { _t: Date.now() })
      .then((r) => {
        if (alive.current && r.status === 'ok') setMeta({ ...EMPTY_META, ...r });
      })
      .catch(() => {});
    load(0, PRODUCT_DEFAULT, 1, 'sku', 24);
    return () => {
      alive.current = false;
      clearTimeout(timer.current);
      clearTimeout(toastTimer.current);
    };
  }, []);

  /* เปลี่ยนตัวกรอง → หน้า 1 · ช่องพิมพ์หน่วง 400ms · ช่องเลือกยิงทันที */
  const applyFilter = (which, next, { debounce } = {}) => {
    (which === 0 ? setPf : setCf)(next);
    clearTimeout(timer.current);
    if (debounce) {
      pending.current = { which, next };
      timer.current = setTimeout(() => {
        pending.current = null;
        setPage(1);
        load(which, next, 1, sort[which], perPage);
      }, 400);
    } else {
      pending.current = null;
      setPage(1);
      load(which, next, 1, sort[which], perPage);
    }
  };
  const changeTab = (v) => {
    clearTimeout(timer.current);
    pending.current = null;
    setTab(v);
    setPage(1);
    setRows([]);
    setSummary(null);
    load(v, filterOf(v), 1, sort[v], perPage);
  };
  const changePage = (p) => {
    clearTimeout(timer.current);
    const f =
      pending.current && pending.current.which === tab ? pending.current.next : filterOf(tab);
    const target = pending.current ? 1 : p;
    pending.current = null;
    setPage(target);
    load(tab, f, target, sort[tab], perPage);
  };
  const changeSort = (s) => {
    const next = tab === 0 ? [s, sort[1]] : [sort[0], s];
    setSort(next);
    setPage(1);
    load(tab, filterOf(tab), 1, s, perPage);
  };
  const changePerPage = (n) => {
    setPerPage(n);
    setPage(1);
    load(tab, filterOf(tab), 1, sort[tab], n);
  };
  const changeView = (v) => {
    if (!v) return;
    setView(v);
    try {
      localStorage.setItem('image_export.view', v);
    } catch (e) {
      /* โหมดส่วนตัว/ปิด storage — จำไม่ได้ก็ใช้งานต่อได้ */
    }
  };
  const resetFilter = () => applyFilter(tab, tab === 0 ? PRODUCT_DEFAULT : CAMPAIGN_DEFAULT);
  const removeChip = (key) => {
    const f = filterOf(tab);
    const def = tab === 0 ? PRODUCT_DEFAULT : CAMPAIGN_DEFAULT;
    const next = { ...f, [key]: def[key] };
    /* ลูกของหมวดต้องล้างตามแม่ */
    if (key === 'dep_id') Object.assign(next, { cat_id: 0, grp_id: 0, sub_id: 0 });
    if (key === 'cat_id') Object.assign(next, { grp_id: 0, sub_id: 0 });
    if (key === 'grp_id') Object.assign(next, { sub_id: 0 });
    applyFilter(tab, next);
  };
  const onPreset = (p, on) => {
    const f = filterOf(tab);
    const def = tab === 0 ? PRODUCT_DEFAULT : CAMPAIGN_DEFAULT;
    const next = { ...f };
    Object.keys(p.patch).forEach((k) => {
      next[k] = on ? def[k] : p.patch[k];
    });
    applyFilter(tab, next);
  };

  /* ───── ที่เลือก ───── */
  const selected = tab === 0 ? products : campaigns;
  const setSelected = tab === 0 ? setProducts : setCampaigns;
  const nProducts = Object.keys(products).length;
  const nCampaigns = Object.keys(campaigns).length;
  const countSel = nProducts + nCampaigns;
  const hasImages = (r) => (tab === 0 ? r.img_count > 0 : r.img_count > 0 || r.product_count > 0);

  const toggle = (row) => {
    if (!selected[row.id] && countSel >= limits.max_select) {
      showNotify(`เลือกได้ไม่เกิน ${limits.max_select} รายการต่อครั้ง`, 'warning');
      return;
    }
    setSelected((prev) => {
      const next = { ...prev };
      if (next[row.id]) delete next[row.id];
      else next[row.id] = row;
      return next;
    });
  };
  const addRows = (candidates, setter) => {
    const cur = setter === setProducts ? products : campaigns;
    const pick = candidates.filter((r) => !cur[r.id]);
    const room = limits.max_select - countSel;
    const add = pick.slice(0, Math.max(0, room));
    if (add.length)
      setter((prev) => {
        const next = { ...prev };
        add.forEach((r) => {
          next[r.id] = r;
        });
        return next;
      });
    return [add.length, pick.length];
  };
  const selectPage = () => {
    /* ข้ามรายการที่ไม่มีรูปอยู่แล้ว (เลือกไปก็ได้ 0 ไฟล์) */
    const [added, wanted] = addRows(
      rows.filter((r) => hasImages(r)),
      setSelected
    );
    if (!added && wanted)
      showNotify(`เลือกได้ไม่เกิน ${limits.max_select} รายการต่อครั้ง`, 'warning');
    else if (added < wanted)
      showNotify(`เลือกเพิ่ม ${added} รายการ · ครบเพดาน ${limits.max_select} แล้ว`, 'warning');
  };
  const selectFiltered = () => {
    if (picking) return;
    if (countSel >= limits.max_select) {
      showNotify(`เลือกได้ไม่เกิน ${limits.max_select} รายการต่อครั้ง`, 'warning');
      return;
    }
    setPicking(true);
    const which = tab;
    FetchData('OPTIONS', `/${mod}/${which === 0 ? 'products' : 'campaigns'}`, app.token, {
      ...bodyOf(which, filterOf(which)),
      ids_only: 'Y',
      sort: sort[which],
      _t: Date.now(),
    })
      .then((r) => {
        if (!alive.current) return;
        setPicking(false);
        if (r.status !== 'ok') {
          showNotify(t(`alert.loaderror`), 'error');
          return;
        }
        const [added, wanted] = addRows(r.data || [], which === 0 ? setProducts : setCampaigns);
        const all = Number(r.total || 0);
        if (!wanted) showNotify('ที่กรองได้เลือกไว้ครบแล้ว', 'info');
        else if (added < wanted || all > wanted)
          showNotify(
            `เลือกเพิ่ม ${added} รายการ · ที่กรองได้ ${all.toLocaleString()} เกินเพดาน ${
              limits.max_select
            } ต่อครั้ง — โหลดชุดนี้แล้วค่อยกรองต่อ`,
            'warning'
          );
        else showNotify(`เลือกเพิ่ม ${added} รายการ`, 'success');
      })
      .catch(() => {
        if (alive.current) {
          setPicking(false);
          showNotify(NET_ERROR, 'error');
        }
      });
  };
  const remove = (kind, id) => {
    (kind === 'p' ? setProducts : setCampaigns)((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };
  const clearAll = () => {
    setProducts({});
    setCampaigns({});
  };
  const setOption = (k, v) => setOpt((o) => ({ ...o, [k]: v }));

  /* ───── สรุปไฟล์ คำนวณเองเมื่อที่เลือก/ตัวเลือกเปลี่ยน (หน่วง 600ms) · ผลเก่าทิ้งทันที ───── */
  useEffect(() => {
    checkSeq.current += 1;
    const mine = checkSeq.current;
    setList(null);
    setExcluded({});
    if (!countSel) {
      setChecking(false);
      return undefined;
    }
    setChecking(true);
    const id = setTimeout(() => {
      FetchData('OPTIONS', `/${mod}/files`, app.token, {
        product_ids: Object.keys(products).map(Number),
        campaign_ids: Object.keys(campaigns).map(Number),
        product_images: opt.productImages ? 'Y' : 'N',
        campaign_images: opt.campaignImages ? 'Y' : 'N',
        main_only: opt.mainOnly ? 'Y' : 'N',
        source: opt.source,
        _t: Date.now(),
      })
        .then((r) => {
          if (!alive.current || mine !== checkSeq.current) return; // เลือกเปลี่ยนระหว่างรอ — รอบใหม่กำลังมา
          setChecking(false);
          if (r.status === 'ok') {
            setList(r);
          } else if (r.msg === 'export_too_many_files') {
            setList({
              error: `ไฟล์รวม ${r.total} ไฟล์ เกินเพดาน ${r.max_files} ไฟล์ต่อครั้ง — ลดรายการที่เลือก`,
            });
          } else if (r.msg === 'export_too_large') {
            setList({
              error: `ไฟล์รวม ≈ ${mb(Number(r.bytes || 0))} เกินเพดาน ${mb(
                Number(r.max_bytes || 0)
              )} ต่อครั้ง — แบ่งดาวน์โหลดเป็นหลายรอบ`,
            });
          } else if (r.msg === 'export_too_many_selected') {
            setList({ error: `เลือกได้ไม่เกิน ${limits.max_select} รายการต่อครั้ง` });
          } else setList({ error: t(`alert.loaderror`) });
        })
        .catch(() => {
          if (alive.current && mine === checkSeq.current) {
            setChecking(false);
            setList({ error: NET_ERROR });
          }
        });
    }, 600);
    return () => clearTimeout(id);
  }, [products, campaigns, opt]);

  const pickedFiles = list && list.files ? list.files.filter((f) => !excluded[f.id]) : [];
  const pickedBytes = pickedFiles.reduce((a, f) => a + f.size, 0);
  const toggleFile = (id) =>
    setExcluded((ex) => {
      const next = { ...ex };
      if (next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
  const setFiles = (ids, off) =>
    setExcluded((ex) => {
      const next = { ...ex };
      ids.forEach((id) => {
        if (off) next[id] = true;
        else delete next[id];
      });
      return next;
    });

  const download = async () => {
    if (!pickedFiles.length || progress) return;
    const zipName = `${
      nCampaigns === 0 ? 'รูปสินค้า' : nProducts === 0 ? 'รูปแคมเปญ' : 'รูป'
    }_${today()}`;
    setProgress([0, pickedFiles.length]);
    try {
      const res = await downloadZip(
        pickedFiles.map((f) => ({ url: f.url, name: f.name, folder: f.folder })),
        zipName,
        (done, all) => alive.current && setProgress([done, all])
      );
      if (alive.current)
        showNotify(zipDoneText(res, 'ไฟล์'), res.skipped.length ? 'warning' : 'success');
    } catch (e) {
      if (alive.current) showNotify('ดาวน์โหลดไม่สำเร็จ ลองอีกครั้ง', 'error');
    } finally {
      if (alive.current) setProgress(null);
    }
  };

  const skippedText = () => {
    if (!list || !list.skipped) return [];
    const s = list.skipped;
    const out = [];
    const few = (arr) =>
      `${arr.slice(0, 5).join(', ')}${arr.length > 5 ? ` และอีก ${arr.length - 5}` : ''}`;
    if ((s.products_no_image || []).length)
      out.push(`ไม่มีรูป (ข้าม): ${few(s.products_no_image)}`);
    if ((s.products_no_source || []).length)
      out.push(
        `ไม่มีรูป${opt.source === 'ai' ? ' AI' : 'ที่ไม่ใช่ AI'} (ข้าม): ${few(
          s.products_no_source
        )}`
      );
    if ((s.campaigns_no_image || []).length)
      out.push(`แคมเปญไม่มีรูปแคมเปญ: ${few(s.campaigns_no_image)}`);
    if ((s.campaigns_all_products || []).length)
      out.push(`ใช้กับสินค้าทุกตัว จึงไม่ดึงรูปสินค้า: ${few(s.campaigns_all_products)}`);
    return out;
  };

  /* ───── render helpers ───── */
  const pages = Math.max(1, Math.ceil(total / perPage));
  const pageAllSelected = rows.length > 0 && rows.every((r) => selected[r.id] || !hasImages(r));
  const presets =
    tab === 0
      ? PRODUCT_PRESETS
      : [...CAMPAIGN_PRESETS, ...(meta.my_branch_id ? [myBranchPreset(meta.my_branch_id)] : [])];
  const chips = tab === 0 ? productChips(pf, meta) : campaignChips(cf, meta);
  const summaryText = () => {
    if (loading) return 'กำลังค้นหา…';
    if (!summary) return `${total.toLocaleString()} รายการ`;
    return tab === 0
      ? `${summary.total.toLocaleString()} สินค้า · มีรูป ${summary.with_image.toLocaleString()} · รูป AI ${summary.with_ai.toLocaleString()}`
      : `${summary.total.toLocaleString()} แคมเปญ · มีรูป ${summary.with_image.toLocaleString()} · กราฟิก AI ${summary.with_ai.toLocaleString()}`;
  };

  const thumb = (r, size) =>
    r.image_url ? (
      <MDBox
        component='img'
        src={r.image_url}
        alt=''
        loading='lazy'
        decoding='async'
        sx={{
          width: size,
          height: size,
          objectFit: 'contain',
          borderRadius: 1,
          display: 'block',
          bgcolor: '#f0f2f5',
        }}
      />
    ) : (
      <MDBox
        sx={{
          width: size,
          height: size,
          borderRadius: 1,
          bgcolor: 'grey.200',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon sx={{ color: 'grey.500' }}>image_not_supported</Icon>
      </MDBox>
    );
  const aiChip = (n, sx) =>
    n > 0 ? (
      <Chip
        size='small'
        color='info'
        icon={<Icon sx={{ fontSize: '12px !important' }}>auto_awesome</Icon>}
        label={`AI ${n}`}
        sx={{ height: 20, fontSize: 11, fontWeight: 700, ...sx }}
      />
    ) : null;
  const statusChip = (s) => {
    const [label, color] = STATUS_CHIP[s] || [s, 'default'];
    return (
      <Chip
        size='small'
        label={label}
        color={color}
        variant={color === 'default' ? 'outlined' : 'filled'}
        sx={{ height: 20, fontSize: 11 }}
      />
    );
  };
  const checkbox = (r, sx) => (
    <Checkbox
      size='small'
      checked={Boolean(selected[r.id])}
      onChange={() => toggle(r)}
      onClick={(e) => e.stopPropagation()}
      inputProps={{ 'aria-label': `เลือก ${tab === 0 ? r.sku : r.code}` }}
      sx={sx}
    />
  );

  const gridCard = (r) => {
    const sel = Boolean(selected[r.id]);
    const code = tab === 0 ? r.sku : r.code;
    return (
      <MDBox
        key={r.id}
        onClick={() => toggle(r)}
        sx={{
          cursor: 'pointer',
          border: '2px solid',
          borderColor: sel ? 'info.main' : '#e9ecef',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: sel ? '#e8f1fd' : '#fff',
          '&:hover': { borderColor: sel ? 'info.main' : '#b9c0cb' },
        }}
      >
        <MDBox sx={{ position: 'relative', aspectRatio: '1 / 1', bgcolor: '#f0f2f5' }}>
          {r.image_url ? (
            <MDBox
              component='img'
              src={r.image_url}
              alt=''
              loading='lazy'
              decoding='async'
              sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          ) : (
            <MDBox
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon sx={{ color: 'grey.500', fontSize: '36px !important' }}>
                image_not_supported
              </Icon>
            </MDBox>
          )}
          {checkbox(r, {
            position: 'absolute',
            top: 2,
            left: 2,
            bgcolor: 'rgba(255,255,255,.85)',
            borderRadius: 1,
            p: 0.25,
          })}
          {aiChip(r.ai_count, { position: 'absolute', top: 6, right: 6 })}
          <Chip
            size='small'
            label={
              tab === 0
                ? `${r.img_count} รูป`
                : `${r.img_count} รูป · ${
                    r.is_all_products === 'Y' ? 'ทุกสินค้า' : `${r.product_count} สินค้า`
                  }`
            }
            sx={{
              position: 'absolute',
              bottom: 6,
              right: 6,
              height: 20,
              fontSize: 11,
              fontWeight: 600,
              bgcolor:
                r.img_count || (tab === 1 && r.product_count)
                  ? 'rgba(52,71,103,.85)'
                  : 'error.main',
              color: '#fff',
            }}
          />
        </MDBox>
        <MDBox px={1} pt={0.75} pb={1}>
          <MDBox display='flex' alignItems='center' justifyContent='space-between' gap={0.5}>
            <MDTypography variant='button' fontWeight='bold' noWrap>
              {code}
            </MDTypography>
            {tab === 1 && statusChip(r.status)}
          </MDBox>
          <MDTypography
            variant='caption'
            display='block'
            title={r.name}
            sx={{
              lineHeight: 1.5, // ไทยต้อง ≥1.5 ไม่งั้น line-clamp ตัดหัววรรณยุกต์ (CLAUDE.md)
              height: '36px',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {r.name}
          </MDTypography>
          <MDTypography variant='caption' color='text' display='block' noWrap>
            {tab === 0
              ? `${r.brand_name || r.cat_name || '—'} · ${
                  r.img_updated ? dateOf(r.img_updated) : 'ไม่มีรูป'
                }`
              : `${dateOf(r.date_start)} – ${dateOf(r.date_stop)} · ${
                  r.all_branch === 'Y' ? 'ทุกสาขา' : `${r.branch_count} สาขา`
                }`}
          </MDTypography>
        </MDBox>
      </MDBox>
    );
  };

  const table = () => (
    <MDBox sx={{ overflowX: 'auto' }}>
      <Table size='small'>
        <TableHead sx={{ display: 'table-header-group' }}>
          <TableRow>
            <TableCell padding='checkbox' />
            <TableCell>รูป</TableCell>
            <TableCell>{tab === 0 ? 'รหัสสินค้า' : 'รหัสแคมเปญ'}</TableCell>
            <TableCell>ชื่อ</TableCell>
            {tab === 0 ? (
              <>
                <TableCell>ยี่ห้อ · หมวด</TableCell>
                <TableCell>รูปอัปเดต</TableCell>
              </>
            ) : (
              <>
                <TableCell>สถานะ</TableCell>
                <TableCell>ช่วงเวลา</TableCell>
                <TableCell>สาขา</TableCell>
              </>
            )}
            <TableCell align='right'>{tab === 0 ? 'รูป' : 'รูปแคมเปญ / สินค้า'}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => {
            const code = tab === 0 ? r.sku : r.code;
            return (
              <TableRow
                key={r.id}
                hover
                selected={Boolean(selected[r.id])}
                onClick={() => toggle(r)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell padding='checkbox'>{checkbox(r)}</TableCell>
                <TableCell>{thumb(r, 44)}</TableCell>
                <TableCell>
                  <MDTypography variant='button' fontWeight='medium' sx={{ whiteSpace: 'nowrap' }}>
                    {code}
                  </MDTypography>
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <MDTypography
                    variant='button'
                    sx={{
                      display: 'block',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={r.name}
                  >
                    {r.name}
                  </MDTypography>
                </TableCell>
                {tab === 0 ? (
                  <>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <MDTypography
                        variant='caption'
                        sx={{
                          display: 'block',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {[r.brand_name, r.cat_name].filter(Boolean).join(' · ') || '—'}
                      </MDTypography>
                    </TableCell>
                    <TableCell>
                      <MDTypography variant='caption' sx={{ whiteSpace: 'nowrap' }}>
                        {r.img_updated ? dateOf(r.img_updated) : '—'}
                      </MDTypography>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>{statusChip(r.status)}</TableCell>
                    <TableCell>
                      <MDTypography variant='caption' sx={{ whiteSpace: 'nowrap' }}>
                        {dateOf(r.date_start)} – {dateOf(r.date_stop)}
                      </MDTypography>
                    </TableCell>
                    <TableCell>
                      <MDTypography variant='caption' sx={{ whiteSpace: 'nowrap' }}>
                        {r.all_branch === 'Y' ? 'ทุกสาขา' : `${r.branch_count} สาขา`}
                      </MDTypography>
                    </TableCell>
                  </>
                )}
                <TableCell align='right'>
                  <MDBox display='flex' alignItems='center' justifyContent='flex-end' gap={0.5}>
                    {aiChip(r.ai_count)}
                    <MDTypography
                      variant='button'
                      color={tab === 0 && !r.img_count ? 'error' : 'text'}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {tab === 0
                        ? r.img_count
                        : `${r.img_count} / ${
                            r.is_all_products === 'Y' ? 'ทุกตัว' : r.product_count
                          }`}
                    </MDTypography>
                  </MDBox>
                </TableCell>
              </TableRow>
            );
          })}
          {!loading && !rows.length && (
            <TableRow>
              <TableCell colSpan={8}>
                <MDTypography variant='button' color='text'>
                  ไม่พบรายการตามตัวกรอง
                </MDTypography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </MDBox>
  );

  const summaryBox = () => {
    if (!countSel)
      return (
        <MDTypography variant='caption' color='text'>
          ติ๊กสินค้าหรือแคมเปญจากด้านซ้าย หรือใช้ “เลือกทั้งหมดที่กรอง”
        </MDTypography>
      );
    if (checking || !list)
      return (
        <MDBox display='flex' alignItems='center' gap={1}>
          <CircularProgress size={14} color='info' />
          <MDTypography variant='caption' color='text'>
            กำลังนับไฟล์…
          </MDTypography>
        </MDBox>
      );
    if (list.error)
      return (
        <MDTypography variant='caption' color='error' fontWeight='bold'>
          {list.error}
        </MDTypography>
      );
    const folders = new Set(list.files.map((f) => f.folder.split('/')[0])).size;
    return (
      <>
        <MDTypography variant='button' fontWeight='bold' display='block'>
          {pickedFiles.length.toLocaleString()}
          {pickedFiles.length !== list.total ? ` จาก ${list.total.toLocaleString()}` : ''} ไฟล์ · ≈{' '}
          {mb(pickedBytes)}
        </MDTypography>
        <MDTypography variant='caption' color='text' display='block'>
          {folders} โฟลเดอร์ · นับใหม่ให้เองเมื่อที่เลือกเปลี่ยน
        </MDTypography>
        {skippedText().map((s) => (
          <MDTypography key={s} variant='caption' color='warning' display='block'>
            {s}
          </MDTypography>
        ))}
      </>
    );
  };

  const downloadLabel = progress ? `กำลังโหลด ${progress[0]}/${progress[1]}` : 'ดาวน์โหลด zip';
  const canDownload = Boolean(list && list.files && pickedFiles.length && !progress);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.product`), 'ดาวน์โหลดรูป']} />
      <MDBox pt={2} pb={{ xs: 12, md: 3 }}>
        <MDBox
          display='flex'
          alignItems='flex-end'
          justifyContent='space-between'
          flexWrap='wrap'
          gap={1}
          mb={2}
        >
          <MDBox>
            <MDTypography variant='h4' fontWeight='bold'>
              ดาวน์โหลดรูป
            </MDTypography>
            <MDTypography variant='button' color='text'>
              กรองให้เหลือชุดที่ต้องการ ติ๊กเลือก แล้วได้ zip เดียว แยกโฟลเดอร์ตามรหัส
            </MDTypography>
          </MDBox>
          <MDBox display='flex' alignItems='center' gap={1} flexWrap='wrap'>
            <MDInput
              select
              size='small'
              label='เรียงตาม'
              value={sort[tab]}
              onChange={(e) => changeSort(e.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ 'aria-label': 'เรียงตาม' }}
              sx={{ minWidth: 170 }}
            >
              {SORTS[tab].map(([k, label]) => (
                <MenuItem key={k} value={k}>
                  {label}
                </MenuItem>
              ))}
            </MDInput>
            <ToggleButtonGroup
              size='small'
              exclusive
              value={view}
              onChange={(e, v) => changeView(v)}
              aria-label='มุมมอง'
            >
              <ToggleButton value='grid' aria-label='มุมมองกริด' sx={{ px: 1.25 }}>
                <Icon fontSize='small'>grid_view</Icon>
              </ToggleButton>
              <ToggleButton value='table' aria-label='มุมมองตาราง' sx={{ px: 1.25 }}>
                <Icon fontSize='small'>view_list</Icon>
              </ToggleButton>
            </ToggleButtonGroup>
          </MDBox>
        </MDBox>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 2, pt: 0 }}>
              <Tabs value={tab} onChange={(e, v) => changeTab(v)} aria-label='เลือกแหล่งรูป'>
                <Tab label={`ตามสินค้า${nProducts ? ` (${nProducts})` : ''}`} />
                <Tab label={`ตามแคมเปญ${nCampaigns ? ` (${nCampaigns})` : ''}`} />
              </Tabs>
              <MDBox mt={1.5}>
                <FilterChips
                  presets={presets}
                  value={filterOf(tab)}
                  onPreset={onPreset}
                  chips={chips}
                  onRemove={removeChip}
                  onClear={resetFilter}
                />
              </MDBox>
              <MDBox mt={1.5}>
                <ExportFilters
                  tab={tab}
                  meta={meta}
                  value={filterOf(tab)}
                  onChange={(next, o) => applyFilter(tab, next, o)}
                />
              </MDBox>
              <MDBox
                display='flex'
                alignItems='center'
                justifyContent='space-between'
                flexWrap='wrap'
                gap={1}
                mt={1.5}
                pt={1.25}
                sx={{ borderTop: '1px dashed #e9ecef' }}
              >
                <MDTypography variant='button' role='status'>
                  {summaryText()}
                </MDTypography>
                <MDBox display='flex' gap={0.75} flexWrap='wrap'>
                  <MDButton
                    size='small'
                    variant='outlined'
                    color='dark'
                    onClick={selectPage}
                    disabled={!rows.length || pageAllSelected}
                  >
                    <Icon>done_all</Icon>&nbsp;เลือกทั้งหน้า
                  </MDButton>
                  <MDButton
                    size='small'
                    variant='outlined'
                    color='info'
                    onClick={selectFiltered}
                    disabled={!total || picking || countSel >= limits.max_select}
                    title={`เลือกรายการที่ตรงตัวกรองและมีรูป ไม่เกิน ${limits.max_select} รายการ`}
                  >
                    {picking ? (
                      <CircularProgress size={14} color='inherit' />
                    ) : (
                      <Icon>playlist_add_check</Icon>
                    )}
                    &nbsp;เลือกทั้งหมดที่กรอง ({Math.min(total, limits.max_select)} แรก)
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>

            <Card sx={{ p: 2, mt: 3 }}>
              {view === 'grid' ? (
                <MDBox sx={{ minHeight: 200 }}>
                  <MDBox
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(148px, 1fr))',
                      gap: 1.5,
                    }}
                  >
                    {rows.map(gridCard)}
                  </MDBox>
                  {!loading && !rows.length && (
                    <MDTypography variant='button' color='text'>
                      ไม่พบรายการตามตัวกรอง
                    </MDTypography>
                  )}
                  {loading && !rows.length && (
                    <MDBox display='flex' justifyContent='center' py={6}>
                      <CircularProgress size={28} color='info' />
                    </MDBox>
                  )}
                </MDBox>
              ) : (
                table()
              )}
              <MDBox
                display='flex'
                alignItems='center'
                justifyContent='space-between'
                flexWrap='wrap'
                gap={1}
                mt={1.5}
                pt={1.25}
                sx={{ borderTop: '1px solid #e9ecef' }}
              >
                <MDBox display='flex' alignItems='center' gap={1}>
                  <MDTypography variant='caption' color='text'>
                    {total
                      ? `แสดง ${((page - 1) * perPage + 1).toLocaleString()}–${Math.min(
                          total,
                          page * perPage
                        ).toLocaleString()} จาก ${total.toLocaleString()}`
                      : ''}
                  </MDTypography>
                  <MDInput
                    select
                    size='small'
                    value={perPage}
                    onChange={(e) => changePerPage(Number(e.target.value))}
                    SelectProps={{ 'aria-label': 'จำนวนต่อหน้า' }}
                    sx={{ width: 110 }}
                  >
                    {PER_PAGE_OPTIONS.map((n) => (
                      <MenuItem key={n} value={n}>
                        {n} ต่อหน้า
                      </MenuItem>
                    ))}
                  </MDInput>
                </MDBox>
                {pages > 1 && (
                  <Pagination
                    count={pages}
                    page={page}
                    onChange={(e, p) => changePage(p)}
                    color='info'
                    size='small'
                  />
                )}
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ p: 2, position: { md: 'sticky' }, top: 96 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='center'>
                <MDTypography variant='h6'>
                  ที่เลือก · {countSel}
                  <MDTypography component='span' variant='h6' color='text' fontWeight='regular'>
                    /{limits.max_select}
                  </MDTypography>
                </MDTypography>
                {countSel > 0 && (
                  <MDButton size='small' variant='text' color='error' onClick={clearAll}>
                    ล้างทั้งหมด
                  </MDButton>
                )}
              </MDBox>
              <MDBox
                display='flex'
                flexWrap='wrap'
                gap={0.5}
                mt={1}
                sx={{ maxHeight: 150, overflowY: 'auto' }}
              >
                {Object.values(products).map((p) => (
                  <Chip
                    key={`p${p.id}`}
                    size='small'
                    label={p.sku}
                    title={p.name}
                    onDelete={() => remove('p', p.id)}
                  />
                ))}
                {Object.values(campaigns).map((c) => (
                  <Chip
                    key={`c${c.id}`}
                    size='small'
                    color='info'
                    variant='outlined'
                    label={c.code}
                    title={c.name}
                    onDelete={() => remove('c', c.id)}
                  />
                ))}
              </MDBox>

              <MDBox
                mt={2}
                pt={1.5}
                sx={{ borderTop: '1px solid #e9ecef' }}
                display='flex'
                flexDirection='column'
                gap={1}
              >
                <MDTypography variant='caption' fontWeight='bold' color='text'>
                  ตัวเลือกไฟล์
                </MDTypography>
                <FormControlLabel
                  sx={{ m: 0, justifyContent: 'space-between' }}
                  labelPlacement='start'
                  control={
                    <Switch
                      size='small'
                      checked={opt.mainOnly}
                      onChange={(e) => setOption('mainOnly', e.target.checked)}
                    />
                  }
                  label={<MDTypography variant='button'>เฉพาะรูปหลักของสินค้า</MDTypography>}
                />
                {nCampaigns > 0 && (
                  <>
                    <FormControlLabel
                      sx={{ m: 0, justifyContent: 'space-between' }}
                      labelPlacement='start'
                      control={
                        <Switch
                          size='small'
                          checked={opt.campaignImages}
                          onChange={(e) => setOption('campaignImages', e.target.checked)}
                        />
                      }
                      label={<MDTypography variant='button'>รูปแคมเปญ (กราฟิก)</MDTypography>}
                    />
                    <FormControlLabel
                      sx={{ m: 0, justifyContent: 'space-between' }}
                      labelPlacement='start'
                      control={
                        <Switch
                          size='small'
                          checked={opt.productImages}
                          onChange={(e) => setOption('productImages', e.target.checked)}
                        />
                      }
                      label={
                        <MDTypography variant='button'>
                          รูปสินค้าในแคมเปญ (โฟลเดอร์ย่อยต่อสินค้า)
                        </MDTypography>
                      }
                    />
                  </>
                )}
                <MDBox>
                  <MDTypography variant='button' display='block' mb={0.5}>
                    ที่มาของรูป
                  </MDTypography>
                  <ToggleButtonGroup
                    size='small'
                    exclusive
                    fullWidth
                    value={opt.source}
                    onChange={(e, v) => v !== null && setOption('source', v)}
                    aria-label='ที่มาของรูป'
                  >
                    <ToggleButton value='' sx={{ textTransform: 'none', fontSize: 12, py: 0.5 }}>
                      ทั้งหมด
                    </ToggleButton>
                    <ToggleButton value='ai' sx={{ textTransform: 'none', fontSize: 12, py: 0.5 }}>
                      เฉพาะรูป AI
                    </ToggleButton>
                    <ToggleButton
                      value='manual'
                      sx={{ textTransform: 'none', fontSize: 12, py: 0.5 }}
                    >
                      ไม่เอารูป AI
                    </ToggleButton>
                  </ToggleButtonGroup>
                </MDBox>
              </MDBox>

              <MDBox mt={2} sx={{ bgcolor: 'grey.100', borderRadius: 1.5, p: 1.5 }} role='status'>
                {summaryBox()}
              </MDBox>

              <MDBox mt={1.5} display='flex' flexDirection='column' gap={1}>
                <MDButton
                  variant='outlined'
                  color='dark'
                  fullWidth
                  disabled={!list || !list.files || !list.total}
                  onClick={() => setReview(true)}
                >
                  <Icon>fact_check</Icon>&nbsp;ดูรายการไฟล์ก่อนโหลด
                  {list && list.files ? ` (${list.total})` : ''}
                </MDButton>
                <MDButton
                  variant='gradient'
                  color='info'
                  fullWidth
                  disabled={!canDownload}
                  onClick={download}
                >
                  <Icon>download</Icon>&nbsp;{downloadLabel}
                </MDButton>
                {progress && (
                  <MDProgress
                    value={Math.round((progress[0] / Math.max(1, progress[1])) * 100)}
                    color='info'
                  />
                )}
              </MDBox>
              <MDTypography variant='caption' color='text' display='block' mt={1.5}>
                โฟลเดอร์ใน zip: รหัสสินค้า/ หรือ รหัสแคมเปญ/รหัสสินค้า/ · ชื่อไฟล์ รหัส_ลำดับ
                (รูปหลัก = _1)
              </MDTypography>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* จอแคบ: แถบล่างติดจอ ให้เห็นจำนวนที่เลือกและกดโหลดได้โดยไม่ต้องเลื่อนลงไปแผงขวา */}
      {countSel > 0 && (
        <MDBox
          sx={{
            display: { xs: 'flex', md: 'none' },
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1100,
            bgcolor: '#fff',
            borderTop: '1px solid #e9ecef',
            boxShadow: '0 -4px 16px rgba(52,71,103,.10)',
            px: 2,
            py: 1.25,
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <MDBox minWidth={0}>
            <MDTypography variant='button' fontWeight='bold' display='block'>
              เลือก {countSel} ·{' '}
              {list && list.files
                ? `≈ ${pickedFiles.length} ไฟล์ · ${mb(pickedBytes)}`
                : 'กำลังนับไฟล์…'}
            </MDTypography>
            <MDButton
              variant='text'
              color='info'
              size='small'
              sx={{ p: 0, minHeight: 0 }}
              disabled={!list || !list.files}
              onClick={() => setReview(true)}
            >
              ดูรายการไฟล์
            </MDButton>
          </MDBox>
          <MDButton variant='gradient' color='info' disabled={!canDownload} onClick={download}>
            <Icon>download</Icon>&nbsp;{progress ? `${progress[0]}/${progress[1]}` : 'ดาวน์โหลด'}
          </MDButton>
        </MDBox>
      )}

      <ExportFileReview
        open={review}
        onClose={() => setReview(false)}
        list={list && list.files ? list : null}
        excluded={excluded}
        onToggle={toggleFile}
        onSetMany={setFiles}
        onDownload={download}
        progress={progress}
      />
      {notify}
      <Footer />
    </DashboardLayout>
  );
}

export default ImageExport;
